import "./ChatPage.scss";
import type React from "react";
import {useMemo, useState} from "react";
import {observer} from "mobx-react-lite";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    InputAdornment,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SearchIcon from '@mui/icons-material/Search';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import {useQueryClient} from "@tanstack/react-query";
import moment from "moment";
import {generatePath, useNavigate} from "react-router";
import {routes} from "../../routing/routes";
import {useStores} from "../../stores";
import {LoadingBarComponent} from "../../components/loading-bar/LoadingBarComponent.tsx";
import {Sidebar} from "./components/Sidebar";
import {useChatContext} from "./context/ChatContext";
import {supersetDashboardsQueryKey, useSupersetDashboards} from "./hooks/useSupersetDashboards";
import {useDeleteDashboardMutation} from "./hooks/useDeleteDashboardMutation";
import type {UserDashboardRecord} from "./types/chatTypes";

const DASHGEN_APP_NAME = "dashgen";

const DashboardCard: React.FC<{
    dashboard: UserDashboardRecord;
    onOpen: () => void;
    onDelete: () => void;
}> = ({dashboard, onOpen, onDelete}) => (
    <Box
        onClick={onOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") onOpen();
        }}
        sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            gap: "3px",
            cursor: "pointer",
            textAlign: "left",
            "&:hover .dashboard-card-thumb": {
                borderColor: "text.disabled",
                boxShadow: "0 4px 14px -6px rgba(0,0,0,0.18)",
            },
            "&:hover .dashboard-card-delete": {opacity: 1},
        }}
    >
        <Box
            className="dashboard-card-thumb"
            sx={{
                position: "relative",
                aspectRatio: "4 / 3",
                mb: "7px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: 1,
                borderColor: "divider",
                borderRadius: "10px",
                bgcolor: "action.hover",
                color: "text.disabled",
                boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
                transition: "box-shadow 0.15s, border-color 0.15s",
            }}
        >
            <SpaceDashboardOutlinedIcon sx={{fontSize: 36}}/>
            <Tooltip title="Remove from list">
                <IconButton
                    className="dashboard-card-delete"
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                    sx={{
                        position: "absolute",
                        top: 6,
                        right: 6,
                        opacity: 0,
                        transition: "opacity 0.12s",
                        bgcolor: "background.paper",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
                        "&:hover": {bgcolor: "background.paper", color: "error.main"},
                    }}
                >
                    <DeleteOutlineIcon fontSize="small"/>
                </IconButton>
            </Tooltip>
        </Box>
        <Typography sx={{fontSize: 11, color: "text.disabled", lineHeight: 1.4}}>
            {moment(dashboard.created_at).format("D MMM YYYY")}
        </Typography>
        <Typography sx={{fontSize: 13.5, fontWeight: 600, color: "text.primary", letterSpacing: "-0.01em"}} noWrap>
            {dashboard.title || "Untitled dashboard"}
        </Typography>
    </Box>
);

const GRID_SX = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(196px, 1fr))",
    gap: "22px 20px",
    pt: "6px",
} as const;

export const ChatDashboardsPage: React.FC = observer(() => {
    const navigate = useNavigate();
    const {bagStore} = useStores();
    const {setSelectedAppName} = useChatContext();
    const [query, setQuery] = useState("");
    const {data: dashboards = [], isLoading} = useSupersetDashboards(bagStore.bagId);
    const queryClient = useQueryClient();
    const {mutate: deleteDashboard, isPending: isDeleting} = useDeleteDashboardMutation();
    const [dashboardToDelete, setDashboardToDelete] = useState<UserDashboardRecord | null>(null);

    const handleConfirmDelete = () => {
        if (!dashboardToDelete) return;
        deleteDashboard(
            {dashboardId: dashboardToDelete.dashboard_id},
            {
                onSettled: () => {
                    setDashboardToDelete(null);
                    void queryClient.invalidateQueries({queryKey: supersetDashboardsQueryKey(bagStore.bagId)});
                },
            },
        );
    };

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return dashboards;
        return dashboards.filter(d => (d.title ?? "").toLowerCase().includes(q));
    }, [dashboards, query]);

    const chatPath = () => generatePath(routes.chat, {id: String(bagStore.bagId)});

    const handleNewDashboard = () => {
        // Maps the design's describe-to-create flow onto the chat: a fresh
        // conversation with the dashboard agent preselected.
        setSelectedAppName(DASHGEN_APP_NAME);
        navigate(chatPath());
    };

    const handleOpen = (dashboard: UserDashboardRecord) => {
        // Open the originating chat session with the embedded dashboard in
        // fullscreen (the panel restores from history; ChatPage applies the flag).
        navigate(
            {pathname: chatPath(), search: `?session_id=${encodeURIComponent(dashboard.session_id)}`},
            {state: {openDashboardFullscreen: true, dashboardUuid: dashboard.embedded_uuid}},
        );
    };

    return (
        <>
            <LoadingBarComponent/>
            <main id="main-component-content">
                <div id="main-page-facade" className="chat-page">
                    <Box
                        className="chat-page-content"
                        sx={{
                            display: "flex",
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            overflow: "hidden",
                        }}
                    >
                        <Sidebar/>
                        <Box sx={{flexGrow: 1, overflowY: "auto", bgcolor: "background.default"}}>
                            <Stack sx={{maxWidth: 880, width: "100%", mx: "auto", px: 5, pt: 4.5, pb: 5, gap: "18px", minHeight: "100%"}}>
                                <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
                                    <Typography component="h1" sx={{fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em"}}>
                                        Dashboards
                                    </Typography>
                                    <Button variant="contained" startIcon={<AddIcon/>} onClick={handleNewDashboard}>
                                        New dashboard
                                    </Button>
                                </Stack>

                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="Search dashboards…"
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small"/>
                                                </InputAdornment>
                                            ),
                                        },
                                    }}
                                />

                                {isLoading ? (
                                    <Box sx={GRID_SX}>
                                        {Array.from({length: 6}, (_, i) => (
                                            <Box key={i}>
                                                <Skeleton variant="rounded" sx={{aspectRatio: "4 / 3", height: "auto", mb: "7px", borderRadius: "10px"}}/>
                                                <Skeleton width="40%" height={16}/>
                                                <Skeleton width="70%" height={20}/>
                                            </Box>
                                        ))}
                                    </Box>
                                ) : filtered.length === 0 ? (
                                    <Stack alignItems="center" justifyContent="center" sx={{flexGrow: 1, gap: "18px", py: "80px"}}>
                                        <SpaceDashboardOutlinedIcon sx={{fontSize: 64, color: "action.disabled"}}/>
                                        <Typography sx={{fontSize: 14, color: "text.secondary"}}>
                                            {dashboards.length === 0
                                                ? "Create your first dashboard"
                                                : "No dashboards match your search"}
                                        </Typography>
                                    </Stack>
                                ) : (
                                    <Box sx={GRID_SX}>
                                        {filtered.map((dashboard) => (
                                            <DashboardCard
                                                key={dashboard.dashboard_id}
                                                dashboard={dashboard}
                                                onOpen={() => handleOpen(dashboard)}
                                                onDelete={() => setDashboardToDelete(dashboard)}
                                            />
                                        ))}
                                    </Box>
                                )}
                            </Stack>
                        </Box>
                    </Box>
                </div>
            </main>
            <Dialog
                open={dashboardToDelete !== null}
                onClose={() => !isDeleting && setDashboardToDelete(null)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Remove dashboard?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {`"${dashboardToDelete?.title || "Untitled dashboard"}" will be removed from your dashboards list. `}
                        The dashboard itself and its chat conversation are kept.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDashboardToDelete(null)} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button color="error" variant="contained" onClick={handleConfirmDelete} disabled={isDeleting}>
                        Remove
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
});
